import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Package,
  History,
  Barcode,
  Plus,
  Search,
  ChevronRight,
  ChevronDown,
  Trash2,
  Info,
  DollarSign,
  Layers,
  Image as ImageIcon,
  Tag,
  Settings,
  HelpCircle,
  CheckCircle2,
  Save,
  FileText,
  Loader2,
  Upload,
  Minus,
  Check,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  PlusCircle,
  Send,
  ScanBarcode,
  Wand2,
  ClipboardList,
  Percent,
  X,
  Copy,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Modal from "@/components/ui/modal";
import ProductCategoryModal from "./components/ProductCategoryModal";
import ProductStockAdjustmentModal from "./components/ProductStockAdjustmentModal";
import ProductSerialBarcodeModal from "./components/ProductSerialBarcodeModal";
import SerialDetailModal from "./components/SerialDetailModal";
import ProductTransactions from "./components/ProductTransactions";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import DefaultEditor from "react-simple-wysiwyg";
import MediaLibraryComponent from "../media/MediaLibraryComponent";
import { ReactSelect } from "@/components/ui/react-select";
import { eventBus } from "../../core/utils/eventBus";
import { cn, formatDate, isStoreChargingTax } from "@/lib/utils";

const IS_SUPERMARKET = import.meta.env.VITE_APP_PROFILE === "supermarket";

const statusVariantMap = {
  processed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  approved: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  draft: "bg-muted text-muted-foreground border-border",
  rejected: "bg-red-500/10 text-red-600 border-red-500/20",
  cancelled: "bg-red-500/10 text-red-600 border-red-500/20",
};

const EditProduct = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (perm) => {
    if (!perm) return true;
    if (!permissions || permissions.length === 0) return false;
    if (Array.isArray(perm)) return perm.some((p) => permissions.includes(p));
    return permissions.includes(perm);
  };

  const [productData, setProductData] = useState({
    name: "",
    description: "",
    short_description: "",
    sku: "",
    type: "simple",
    barcode_symbology: "CODE128",
    barcode: "",
    brand_id: "",
    model_id: "",
    unit_id: "",
    cost: 0,
    store_has_diff_cost: false,
    sell_price: 0,
    store_has_diff_sell_price: false,
    sell_price_min: 0,
    store_has_diff_sell_price_min: false,
    sell_price_max: 0,
    store_has_diff_sell_price_max: false,
    alert_quantity: "",
    store_has_diff_alert_quantity: false,
    warranty_timeframe: "No Warranty",
    warranty: 0,
    imei: "",
    condition: "new",
    upc_code: "",
    is_commission: false,
    display_on_pos: true,
    tax_method: "exclusive",
    is_taxable: true,
    image: "",
    is_serialized: false,
    tracking_type: "none",
    manage_inventory: false,
    has_variant: false,
    is_variant: false,
    is_expirable: false,
    expiry_discount_tiers: [],
    status: "active",
    attributes: null,
    categories: [],
    media: [],
  });

  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categorySearchInput, setCategorySearchInput] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [brandLabels, setBrandLabels] = useState({});
  const [unitSearchInput, setUnitSearchInput] = useState("");
  const [unitSearchTerm, setUnitSearchTerm] = useState("");
  const [unitLabels, setUnitLabels] = useState({});
  const [modelSearchInput, setModelSearchInput] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [modelLabels, setModelLabels] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [activeSection, setActiveSection] = useState("general");

  // Modals state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalInitialName, setCategoryModalInitialName] = useState("");
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [selectedImagesDetails, setSelectedImagesDetails] = useState([]);
  const [showModelModal, setShowModelModal] = useState(false);

  // Stock Adjustment state
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showSerialBarcodeModal, setShowSerialBarcodeModal] = useState(false);
  const [serialBarcodeInitialSerials, setSerialBarcodeInitialSerials] = useState(null);
  const [serialBarcodeInitialMode, setSerialBarcodeInitialMode] = useState(null);
  const [selectedAdjForSerials, setSelectedAdjForSerials] = useState(null);
  const [serialSearchTerm, setSerialSearchTerm] = useState("");
  const [copiedSerial, setCopiedSerial] = useState(null);
  const [selectedTrackingSerial, setSelectedTrackingSerial] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);

  const handleOpenSerialTracking = (serial) => {
    if (!serial) return;
    if (typeof serial === "object" && serial !== null) {
      setSelectedTrackingSerial({
        id: serial.id ? String(serial.id) : undefined,
        serial_number: serial.serial_number || serial.serial || String(serial),
      });
    } else {
      const serialStr = String(serial);
      const adjItem =
        selectedAdjForSerials?.items?.find(
          (it) => String(it.product_id) === String(id)
        ) ||
        selectedAdjForSerials?.items?.[0] ||
        {};
      const rawSerials = Array.isArray(adjItem.serial_numbers)
        ? adjItem.serial_numbers
        : [];
      const match = rawSerials.find((s) =>
        (typeof s === "object" && s !== null
          ? s.serial_number || s.serial
          : String(s)) === serialStr
      );

      setSelectedTrackingSerial({
        id: typeof match === "object" && match?.id ? String(match.id) : undefined,
        serial_number: serialStr,
      });
    }
    setShowTrackingModal(true);
  };

  const handleOpenSerialBarcodeModal = (serials = null) => {
    if (Array.isArray(serials) && serials.length > 0) {
      setSerialBarcodeInitialSerials(serials);
      setSerialBarcodeInitialMode("serials");
    } else {
      setSerialBarcodeInitialSerials(null);
      setSerialBarcodeInitialMode("product");
    }
    setShowSerialBarcodeModal(true);
  };

  // Form Data for Modals
  const [brandFormData, setBrandFormData] = useState({
    name: "",
    description: "",
  });
  const [unitFormData, setUnitFormData] = useState({
    name: "",
    short_name: "",
    is_active: true,
  });
  const [modelFormData, setModelFormData] = useState({
    name: "",
    brand_id: "",
    description: "",
    status: "active",
  });

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [markupPercent, setMarkupPercent] = useState("");
  const [useSamePriceForMinMax, setUseSamePriceForMinMax] = useState(true);

  const currentStore = useSelector((state) => state.current_store);
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const storeChargesTax = isStoreChargingTax(currentStore);
  const storeTaxPercentage = parseFloat(currentStore?.tax_percentage || 0);
  const sellPriceNum = parseFloat(productData.sell_price) || 0;
  const costPriceNum = parseFloat(productData.cost) || 0;
  const effectiveTaxRate = productData.is_taxable && storeChargesTax ? storeTaxPercentage : 0;
  const taxAmount =
    effectiveTaxRate > 0 && sellPriceNum > 0
      ? productData.tax_method === "inclusive"
        ? (sellPriceNum * effectiveTaxRate) / (100 + effectiveTaxRate)
        : (sellPriceNum * effectiveTaxRate) / 100
      : 0;
  const finalPriceWithTax =
    effectiveTaxRate > 0 && sellPriceNum > 0
      ? productData.tax_method === "inclusive"
        ? sellPriceNum
        : sellPriceNum + taxAmount
      : sellPriceNum;

  const barcodeFormats = [
    { value: "CODE128", label: "Code 128" },
    { value: "CODE39", label: "Code 39" },
    { value: "EAN13", label: "EAN-13" },
    { value: "EAN8", label: "EAN-8" },
    { value: "UPC", label: "UPC" },
  ];

  const conditionOptions = [
    { value: "new", label: "Brand New" },
    { value: "used", label: "Pre-Owned/Refurbished" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const warrantyOptions = [
    { value: "No Warranty", label: "None" },
    { value: "Days", label: "Days" },
    { value: "Months", label: "Months" },
    { value: "Years", label: "Years" },
    { value: "Lifetime", label: "Lifetime" },
  ];

  const taxMethodOptions = [
    { value: "exclusive", label: "Exclusive (Price + Tax)" },
    { value: "inclusive", label: "Inclusive (Price includes Tax)" },
  ];

  const trackingTypeOptions = [
    { value: "none", label: "None" },
    { value: "standard", label: "Standard / Batch" },
    { value: "serial", label: "Serial Number" },
  ];



  const sections = useMemo(
    () => {
      const baseSections = [
        { id: "general", label: "General Info", icon: Info },
        { id: "organization", label: "Organization", icon: Tag },
        { id: "pricing", label: "Pricing & Tax", icon: DollarSign },
        { id: "inventory", label: "Inventory", icon: Layers },
      ];

      if (productData.manage_inventory && productData.tracking_type !== "none") {
        baseSections.push({ id: "stockAdjustment", label: "Stock Adjustment", icon: ClipboardList });
      }

      if (
        Boolean(productData.manage_inventory) &&
        productData.tracking_type &&
        productData.tracking_type !== "none"
      ) {
        baseSections.push({ id: "transactions", label: "Transactions", icon: History });
      }

      baseSections.push({ id: "media", label: "Product Media", icon: ImageIcon });

      return baseSections;
    },
    [productData.manage_inventory, productData.tracking_type],
  );

  const generalRef = useRef(null);
  const organizationRef = useRef(null);
  const pricingRef = useRef(null);
  const inventoryRef = useRef(null);
  const stockAdjustmentRef = useRef(null);
  const transactionsRef = useRef(null);
  const mediaRef = useRef(null);

  const sectionRefs = useMemo(
    () => ({
      general: generalRef,
      organization: organizationRef,
      pricing: pricingRef,
      inventory: inventoryRef,
      stockAdjustment: stockAdjustmentRef,
      transactions: transactionsRef,
      media: mediaRef,
    }),
    [],
  );

  const handleCostChange = (val) => {
    const costNum = parseFloat(val);
    const markupNum = parseFloat(markupPercent);

    let updated = {
      ...productData,
      cost: val,
    };

    if (!isNaN(costNum) && costNum > 0 && !isNaN(markupNum) && markupPercent !== "") {
      const newSell = Number((costNum * (1 + markupNum / 100)).toFixed(2));
      updated.sell_price = newSell;
      if (useSamePriceForMinMax) {
        updated.sell_price_min = newSell;
        updated.sell_price_max = newSell;
      }
    } else if (!isNaN(costNum) && costNum > 0 && productData.sell_price) {
      const sellNum = parseFloat(productData.sell_price);
      if (!isNaN(sellNum)) {
        setMarkupPercent((((sellNum - costNum) / costNum) * 100).toFixed(2));
      }
    }

    setProductData(updated);
  };

  const handleMarkupChange = (val) => {
    setMarkupPercent(val);
    const markupNum = parseFloat(val);
    const costNum = parseFloat(productData.cost);

    if (!isNaN(costNum) && costNum > 0 && !isNaN(markupNum) && val !== "") {
      const newSell = Number((costNum * (1 + markupNum / 100)).toFixed(2));
      setProductData((prev) => ({
        ...prev,
        sell_price: newSell,
        ...(useSamePriceForMinMax
          ? { sell_price_min: newSell, sell_price_max: newSell }
          : {}),
      }));
    }
  };

  const handleSellingPriceChange = (val) => {
    const sellNum = parseFloat(val);
    const costNum = parseFloat(productData.cost);

    if (!isNaN(costNum) && costNum > 0 && !isNaN(sellNum) && val !== "") {
      setMarkupPercent((((sellNum - costNum) / costNum) * 100).toFixed(2));
    } else if (!val || isNaN(sellNum)) {
      setMarkupPercent("");
    }

    setProductData((prev) => ({
      ...prev,
      sell_price: val,
      ...(useSamePriceForMinMax
        ? { sell_price_min: val, sell_price_max: val }
        : {}),
    }));
  };

  const handleToggleSamePriceForMinMax = (checked) => {
    const isChecked = Boolean(checked);
    setUseSamePriceForMinMax(isChecked);
    if (isChecked) {
      setProductData((prev) => ({
        ...prev,
        sell_price_min: prev.sell_price,
        sell_price_max: prev.sell_price,
      }));
    }
  };

  // Queries
  const { data: productDataFromQuery, isLoading: isProductLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.PRODUCT_DETAILS(id),
      );
      return res.data.data;
    },
    enabled: !!id,
  });

  const { data: brands = [], isFetching: isBrandsFetching } = useQuery({
    queryKey: ["brands", brandSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS, {
        params: {
          perPage: 15,
          search: brandSearchTerm || undefined,
        },
      });
      return res.data.data;
    },
    enabled: hasPermission("brand read"),
    staleTime: 30000,
  });
  const { data: units = [], isFetching: isUnitsFetching } = useQuery({
    queryKey: ["units", unitSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.UNITS, {
        params: {
          perPage: 15,
          search: unitSearchTerm || undefined,
        },
      });
      return res.data.data;
    },
  });
  const {
    data: rawCategories = [],
    isLoading: isCategoriesLoading,
    isFetching: isCategoriesFetching,
  } = useQuery({
    queryKey: ["categories", "all", categorySearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: {
          search: categorySearchTerm || undefined,
          perPage: 9999,
        },
      });
      return res.data?.data || res.data || [];
    },
  });
  const categories = useMemo(() => {
    if (Array.isArray(rawCategories)) return rawCategories;
    if (Array.isArray(rawCategories?.data)) return rawCategories.data;
    return [];
  }, [rawCategories]);
  const { data: models = [], isFetching: isModelsFetching } = useQuery({
    queryKey: ["models", productData.brand_id, modelSearchTerm],
    queryFn: async () => {
      if (!productData.brand_id) return [];
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.BRAND_MODELS(productData.brand_id),
        {
          params: {
            perPage: 15,
            search: modelSearchTerm || undefined,
          },
        },
      );
      return res.data.data.filter((model) => model.status === "active");
    },
    enabled: !!productData.brand_id && hasPermission("brand model read"),
  });


  // Stock adjustment history for this product
  const { data: adjHistoryData, isLoading: isAdjHistoryLoading } = useQuery({
    queryKey: ["stock-adj-history", id, currentStore?.id],
    queryFn: async () => {
      if (!id || !currentStore?.id) return { data: [] };
      const res = await apiClient.get(API_ENDPOINTS.STOCK.PRODUCT_HISTORY, {
        params: { product_id: id, store_id: currentStore.id, per_page: 20 },
      });
      return res.data;
    },
    enabled: !!id && !!currentStore?.id && hasPermission("stock adjustment read"),
  });
  const adjHistory = adjHistoryData?.data || [];





  useEffect(() => {
    if (productDataFromQuery) {
      const sanitizedData = { ...productDataFromQuery };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === null) sanitizedData[key] = "";
      });
      if (!sanitizedData.condition) {
        sanitizedData.condition = "new";
      } else if (typeof sanitizedData.condition === "string") {
        sanitizedData.condition = sanitizedData.condition.toLowerCase();
      }

      const cost = parseFloat(sanitizedData.cost);
      const sell = parseFloat(sanitizedData.sell_price);
      if (!isNaN(cost) && cost > 0 && !isNaN(sell)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMarkupPercent((((sell - cost) / cost) * 100).toFixed(2));
      } else {
        setMarkupPercent("");
      }

      const sellMin =
        sanitizedData.sell_price_min !== undefined &&
          sanitizedData.sell_price_min !== null &&
          sanitizedData.sell_price_min !== ""
          ? parseFloat(sanitizedData.sell_price_min)
          : null;
      const sellMax =
        sanitizedData.sell_price_max !== undefined &&
          sanitizedData.sell_price_max !== null &&
          sanitizedData.sell_price_max !== ""
          ? parseFloat(sanitizedData.sell_price_max)
          : null;
      const isSame =
        (!sellMin || sellMin === sell) && (!sellMax || sellMax === sell);
      setUseSamePriceForMinMax(isSame);

      setProductData(sanitizedData);
      // Removed unused selectedImageDetails code
      if (sanitizedData.categories) {
        setSelectedCategoryIds(sanitizedData.categories.map((cat) => cat.id));
      }
      if (sanitizedData.brand) {
        setBrandLabels((prev) => ({
          ...prev,
          [String(sanitizedData.brand.id)]: sanitizedData.brand.name,
        }));
      }
      if (sanitizedData.unit) {
        setUnitLabels((prev) => ({
          ...prev,
          [String(sanitizedData.unit.id)]: sanitizedData.unit.name,
        }));
      }
      if (sanitizedData.model) {
        setModelLabels((prev) => ({
          ...prev,
          [String(sanitizedData.model.id)]: sanitizedData.model.name,
        }));
      }
    }
  }, [productDataFromQuery]);

  // Mutations
  const updateProductMutation = useMutation({
    mutationFn: async (submitData) => {
      const d = { ...submitData };
      const res = await apiClient.put(
        API_ENDPOINTS.INVENTORY.PRODUCT_DETAILS(id),
        d,
      );
      return res.data.data;
    },
    onSuccess: (updatedProduct) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      toast.success("Product updated successfully");
      eventBus.publish("product:updated", updatedProduct);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to update product"),
  });

  const createBrandMutation = useMutation({
    mutationFn: async (data) =>
      (await apiClient.post(API_ENDPOINTS.INVENTORY.BRANDS, data)).data.data,
    onSuccess: (newBrand) => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand created");
      setBrandLabels((prev) => ({
        ...prev,
        [String(newBrand.id)]: newBrand.name,
      }));
      setProductData({ ...productData, brand_id: newBrand.id });
      setShowBrandModal(false);
    },
  });

  const createUnitMutation = useMutation({
    mutationFn: async (data) =>
      (await apiClient.post(API_ENDPOINTS.INVENTORY.UNITS, data)).data.data,
    onSuccess: (newUnit) => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      toast.success("Unit created");
      setUnitLabels((prev) => ({
        ...prev,
        [String(newUnit.id)]: newUnit.name,
      }));
      setProductData({ ...productData, unit_id: newUnit.id });
      setShowUnitModal(false);
    },
  });

  const createModelMutation = useMutation({
    mutationFn: async (modelData) => {
      const res = await apiClient.post(
        API_ENDPOINTS.INVENTORY.BRAND_MODELS(modelData.brand_id),
        modelData,
      );
      return res.data.data;
    },
    onSuccess: (newModel) => {
      queryClient.invalidateQueries({
        queryKey: ["models", productData.brand_id],
      });
      toast.success("Model created!");
      setModelLabels((prev) => ({
        ...prev,
        [String(newModel.id)]: newModel.name,
      }));
      setProductData({ ...productData, model_id: newModel.id.toString() });
      setShowModelModal(false);
    },
  });

  const removeMediaMutation = useMutation({
    mutationFn: async (mediaLibraryId) => {
      await apiClient.delete(API_ENDPOINTS.MEDIA.REMOVE, {
        params: { media_library_id: mediaLibraryId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      toast.success("Media removed successfully");
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to remove media"),
  });

  // ---- Stock Adjustment Handlers ----
  const openAdjustmentModal = () => {
    setShowAdjustmentModal(true);
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = sectionRefs[sectionId].current;
    if (element) {
      const offset = 150; // Accounts for Header + PageHeader padding
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Debounce category search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCategorySearchTerm(categorySearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [categorySearchInput]);

  // Debounce brand search
  useEffect(() => {
    const timer = setTimeout(() => {
      setBrandSearchTerm(brandSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [brandSearchInput]);

  // Debounce unit search
  useEffect(() => {
    const timer = setTimeout(() => {
      setUnitSearchTerm(unitSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [unitSearchInput]);

  // Debounce model search
  useEffect(() => {
    const timer = setTimeout(() => {
      setModelSearchTerm(modelSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [modelSearchInput]);



  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const ref = sectionRefs[section.id].current;
        if (ref) {
          const { offsetTop, offsetHeight } = ref;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section.id);
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections, sectionRefs]);

  useEffect(() => {
    const unsubMediaAssign = eventBus.subscribe(
      "product:updated",
      async (product) => {
        const imagesToAssign = productData.images || [];
        if (product.id && imagesToAssign.length > 0) {
          for (const media_id of imagesToAssign) {
            try {
              await apiClient.post(API_ENDPOINTS.MEDIA.ASSIGN, {
                media_id: media_id,
                model_type: "Product",
                model_id: product.id,
                collection_name: "product",
                name: product.name,
                description: null,
                custom_properties: [],
              });
            } catch (error) {
              console.error("Error assigning media:", error);
            }
          }
          // Clear buffer after successful save if needed, or just let page reload/state update handle it
          setSelectedImagesDetails([]);
        }
      },
    );
    return () => unsubMediaAssign();
  }, [productData.images, productData.name]);

  const handleSubmit = () => {
    if (!productData.name) return toast.error("Product name is required");
    if (
      productData.cost === undefined ||
      productData.cost === null ||
      productData.cost === ""
    )
      return toast.error("Cost price is required");
    if (
      productData.sell_price === undefined ||
      productData.sell_price === null ||
      productData.sell_price === ""
    )
      return toast.error("Sell price is required");

    const effectiveMin = useSamePriceForMinMax
      ? productData.sell_price
      : productData.sell_price_min;
    const effectiveMax = useSamePriceForMinMax
      ? productData.sell_price
      : productData.sell_price_max;

    if (
      effectiveMin === undefined ||
      effectiveMin === ""
    )
      return toast.error("Minimum sell price is required");
    if (
      effectiveMax === undefined ||
      effectiveMax === ""
    )
      return toast.error("Maximum sell price is required");

    if (productData.manage_inventory) {
      if (
        !productData.tracking_type ||
        !["standard", "serial"].includes(productData.tracking_type)
      ) {
        return toast.error(
          "Tracking type must be 'standard' or 'serial' when managing inventory",
        );
      }
    } else if (
      productData.tracking_type &&
      productData.tracking_type !== "none"
    ) {
      return toast.error(
        `Manage inventory must be enabled when tracking type is ${productData.tracking_type}`,
      );
    }

    const submitData = {
      ...productData,
      cost: parseFloat(productData.cost) || 0,
      sell_price: parseFloat(productData.sell_price) || 0,
      sell_price_min: useSamePriceForMinMax
        ? parseFloat(productData.sell_price) || 0
        : parseFloat(productData.sell_price_min) || 0,
      sell_price_max: useSamePriceForMinMax
        ? parseFloat(productData.sell_price) || 0
        : parseFloat(productData.sell_price_max) || 0,
      categories: selectedCategoryIds.length > 0 ? selectedCategoryIds : null,
    };

    // Only allow changing tracking_type if it was originally 'none'
    // If it's already set to standard or serial, we don't send it to avoid backend errors
    const originalTrackingType = productDataFromQuery?.tracking_type || "none";
    if (originalTrackingType !== "none") {
      delete submitData.tracking_type;
    }

    updateProductMutation.mutate(submitData);
  };

  const buildCategoryHierarchy = (categories) => {
    const categoryMap = new Map();

    // Helper to recursively add a category and its parent/children to the map
    const addCategoryToMap = (cat) => {
      if (!cat) return;
      if (!categoryMap.has(cat.id)) {
        categoryMap.set(cat.id, { ...cat, children: [] });
      }
      if (cat.parent) {
        addCategoryToMap(cat.parent);
      }
      if (cat.children && cat.children.length > 0) {
        cat.children.forEach((child) => addCategoryToMap(child));
      }
    };

    // Populate the map with all categories and their relations (both parent and children)
    categories.forEach((cat) => addCategoryToMap(cat));

    // Reconstruct the nested hierarchy tree
    const rootCategories = [];
    categoryMap.forEach((catNode) => {
      if (catNode.parent_id === null) {
        rootCategories.push(catNode);
      } else {
        const parentNode = categoryMap.get(catNode.parent_id);
        if (parentNode) {
          if (!parentNode.children.some((child) => child.id === catNode.id)) {
            parentNode.children.push(catNode);
          }
        } else {
          // Fallback if parent node is not in the map
          rootCategories.push(catNode);
        }
      }
    });

    return rootCategories;
  };

  const filterCategories = (categories, searchTerm) => {
    if (!searchTerm) return categories;
    const term = searchTerm.toLowerCase();
    const filterRecursive = (cats) =>
      cats.reduce((acc, cat) => {
        const matches = cat.name.toLowerCase().includes(term);
        const childMatches = filterRecursive(cat.children || []);
        if (matches || childMatches.length > 0)
          acc.push({ ...cat, children: childMatches });
        return acc;
      }, []);
    return filterRecursive(categories);
  };

  const hasSelectedDescendant = (cat, selectedIds) => {
    if (selectedIds.includes(cat.id)) return true;
    if (cat.children && cat.children.length > 0) {
      return cat.children.some((child) =>
        hasSelectedDescendant(child, selectedIds),
      );
    }
    return false;
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderCategoryCheckboxes = (categories, level = 0) => {
    return categories.map((category) => {
      const isExpanded = categorySearchTerm
        ? true
        : expandedIds.has(category.id) ||
        hasSelectedDescendant(category, selectedCategoryIds);
      const hasChildren = category.children && category.children.length > 0;

      return (
        <div key={category.id} style={{ marginLeft: `${level * 20}px` }}>
          <div className="flex items-center space-x-2 py-1">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggleExpand(category.id)}
                className="p-1 hover:bg-muted rounded text-muted-foreground transition-colors"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="w-6" />
            )}
            <Checkbox
              id={`cat-${category.id}`}
              checked={selectedCategoryIds.includes(category.id)}
              onCheckedChange={(checked) => {
                if (checked)
                  setSelectedCategoryIds([...selectedCategoryIds, category.id]);
                else
                  setSelectedCategoryIds(
                    selectedCategoryIds.filter((id) => id !== category.id),
                  );
              }}
            />
            <Label htmlFor={`cat-${category.id}`} className="cursor-pointer">
              {category.name}
            </Label>
          </div>
          {hasChildren &&
            isExpanded &&
            renderCategoryCheckboxes(category.children, level + 1)}
        </div>
      );
    });
  };

  const filteredCategoryTree = useMemo(() => {
    return filterCategories(
      buildCategoryHierarchy(categories),
      categorySearchTerm,
    );
  }, [categories, categorySearchTerm]);

  const trimmedCategorySearch = categorySearchInput.trim();

  const hasExactCategoryMatch = useMemo(() => {
    if (!trimmedCategorySearch) return false;
    const term = trimmedCategorySearch.toLowerCase();
    return categories.some(
      (c) => c?.name && c.name.trim().toLowerCase() === term,
    );
  }, [trimmedCategorySearch, categories]);

  const handleOpenCategoryModal = (name = "") => {
    setCategoryModalInitialName(name);
    setShowCategoryModal(true);
  };

  const isCategoriesBusy =
    isCategoriesLoading ||
    (isCategoriesFetching && categories.length === 0);

  if (isProductLoading)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="bg-muted/10 min-h-[calc(100vh-4rem)]">
      {/* Top Header */}
      <div className="sticky top-16 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/products">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                Edit Product
              </h1>
              <p className="text-sm text-muted-foreground">
                {productData.name || "Loading..."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/products">Cancel</Link>
            </Button>
            {Boolean(productData.manage_inventory) &&
              productData.tracking_type &&
              productData.tracking_type !== "none" &&
              (hasPermission("stock adjustment create") ||
                hasPermission("product update")) && (
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={openAdjustmentModal}
                >
                  <Package className="h-4 w-4" />
                  Update Stock
                </Button>
              )}
            {hasPermission("report serial barcode view") &&
              Boolean(productDataFromQuery?.manage_inventory ?? productData.manage_inventory) &&
              (productDataFromQuery?.tracking_type === "serial" ||
                productData.tracking_type === "serial" ||
                Boolean(productDataFromQuery?.is_serialized || productData.is_serialized)) && (
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => handleOpenSerialBarcodeModal(null)}
                >
                  <ScanBarcode className="h-4 w-4" />
                  Serial Barcode
                </Button>
              )}
            <Button
              variant="default"
              className="shadow-md"
              onClick={handleSubmit}
              disabled={updateProductMutation.isPending}
            >
              {updateProductMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Update Product
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8 relative">
        <div className="flex gap-8 items-start">
          {/* Navigation Sidebar */}
          <div className="hidden lg:block w-64 shrink-0 sticky top-40 h-fit">
            <div className="space-y-1">
              <p className="px-3 mb-4 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                Manage
              </p>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group",
                    activeSection === section.id
                      ? "bg-primary/10 text-primary shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <section.icon
                    className={cn(
                      "h-4 w-4",
                      activeSection === section.id
                        ? "text-primary"
                        : "text-muted-foreground group-hover:text-foreground",
                    )}
                  />
                  {section.label}
                  {activeSection === section.id && (
                    <div className="ml-auto w-1.5 h-1.5 bg-primary rounded-full shadow-[0_0_8px_rgba(var(--primary),0.6)]" />
                  )}
                </button>
              ))}

              <div className="mt-8 p-4 bg-primary/5 rounded-xl border border-primary/10">
                <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-xs uppercase tracking-wider">
                  <HelpCircle className="h-3.5 w-3.5" />
                  Quick Tips
                </div>
                <ul className="space-y-2 text-[11px] text-muted-foreground leading-relaxed">
                  <li className="flex gap-2">
                    <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                    Hover over icons next to labels for more info
                  </li>
                  <li className="flex gap-2">
                    <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                    Last edited on {formatDate(new Date())}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Main Content Sections */}
          <div className="flex-1 space-y-10 pb-24">
            {/* General Info */}
            <section ref={generalRef} id="general">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100/10 text-blue-600 dark:text-blue-400 rounded-lg">
                      <Info className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Basic Information</CardTitle>
                      <CardDescription>
                        Primary product identifiers and settings
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-6">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className="text-foreground">Product Name *</Label>
                      <Input
                        value={productData.name}
                        onChange={(e) =>
                          setProductData({
                            ...productData,
                            name: e.target.value,
                          })
                        }
                        className="bg-muted/30 focus:bg-card h-11 text-lg font-medium"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-foreground">SKU</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p className="text-xs">
                                Unique identifier (e.g. PRD-001)
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Input
                          value={productData.sku}
                          onChange={(e) =>
                            setProductData({
                              ...productData,
                              sku: e.target.value,
                            })
                          }
                          className="bg-muted/30 focus:bg-card"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Condition</Label>
                        <ReactSelect
                          options={conditionOptions}
                          value={conditionOptions.find(
                            (opt) =>
                              opt.value === productData.condition ||
                              (opt.value === "used" &&
                                [
                                  "used",
                                  "pre-owned",
                                  "preowned",
                                  "refurbished",
                                ].includes(
                                  productData.condition?.toLowerCase?.(),
                                )),
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              condition: opt.value,
                            })
                          }
                          placeholder="Select condition"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <ReactSelect
                          options={statusOptions}
                          value={statusOptions.find(
                            (opt) => opt.value === productData.status,
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              status: opt.value,
                            })
                          }
                          placeholder="Select status"
                        />
                      </div>
                    </div>

                    {/* Short Description & Modal Trigger */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-foreground font-medium">
                          Short Description
                        </Label>
                        <Button
                          type="button"
                          variant="link"
                          className="p-0 h-auto text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1.5"
                          onClick={() => setShowDescriptionModal(true)}
                        >
                          <FileText className="h-3.5 w-3.5" />
                          {productData.description
                            ? "Edit Detailed Description"
                            : "+ Add Detailed Description"}
                        </Button>
                      </div>
                      <Textarea
                        rows={3}
                        placeholder="Brief summary or highlight of the product..."
                        value={productData.short_description || ""}
                        onChange={(e) => {
                          if (e.target.value.length <= 255) {
                            setProductData({
                              ...productData,
                              short_description: e.target.value,
                            });
                          }
                        }}
                        className="bg-muted/30 focus:bg-card resize-none"
                      />
                      <div className="flex justify-between items-center text-[11px] text-muted-foreground">
                        <span>Appears in list views, receipts and search results</span>
                        <span>{(productData.short_description || "").length} / 255</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl border border-dashed border-border">
                      <Switch
                        checked={productData.display_on_pos}
                        onCheckedChange={(c) =>
                          setProductData({ ...productData, display_on_pos: c })
                        }
                        id="pos"
                      />
                      <Label htmlFor="pos" className="cursor-pointer">Display on POS</Label>
                    </div>

                    {/* Collapsible Accordion for Barcode, UPC, IMEI, Symbology, Warranty, Commission */}
                    <Accordion
                      type="single"
                      collapsible
                      className="w-full border border-border/70 rounded-xl overflow-hidden bg-muted/10"
                    >
                      <AccordionItem value="identifiers" className="border-none">
                        <AccordionTrigger className="px-5 py-3.5 hover:no-underline font-medium text-sm text-foreground hover:bg-muted/20">
                          <div className="flex items-center gap-2.5">
                            <Barcode className="h-4 w-4 text-primary" />
                            <span>Additional Information</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-5 pb-5 pt-2 space-y-6">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label>Barcode Number</Label>
                              <Input
                                value={productData.barcode || ""}
                                onChange={(e) =>
                                  setProductData({
                                    ...productData,
                                    barcode: e.target.value,
                                  })
                                }
                                placeholder="Barcode Number"
                                className="bg-muted/30 focus:bg-card"
                              />
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label>Barcode Symbology</Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p className="text-xs">
                                      Select format for barcode generation
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <ReactSelect
                                options={barcodeFormats}
                                value={barcodeFormats.find(
                                  (opt) => opt.value === productData.barcode_symbology,
                                )}
                                onChange={(opt) =>
                                  setProductData({
                                    ...productData,
                                    barcode_symbology: opt.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label>UPC Code</Label>
                              <Input
                                value={productData.upc_code || ""}
                                onChange={(e) =>
                                  setProductData({
                                    ...productData,
                                    upc_code: e.target.value,
                                  })
                                }
                                placeholder="Universal Product Code"
                                className="bg-muted/30 focus:bg-card"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>IMEI</Label>
                              <Input
                                value={productData.imei || ""}
                                onChange={(e) =>
                                  setProductData({
                                    ...productData,
                                    imei: e.target.value,
                                  })
                                }
                                placeholder="IMEI"
                                className="bg-muted/30 focus:bg-card"
                              />
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-6 items-end">
                            <div className="space-y-2">
                              <Label className="text-foreground">
                                Warranty Coverage
                              </Label>
                              <div className="flex gap-2 p-1.5 bg-muted/30 rounded-lg border border-border text-sm">
                                <Input
                                  type="number"
                                  className="w-20 bg-background border-none shadow-none h-8 text-center"
                                  value={productData.warranty}
                                  min="0"
                                  onChange={(e) =>
                                    setProductData({
                                      ...productData,
                                      warranty: Math.max(0, e.target.value),
                                    })
                                  }
                                />
                                <div className="w-px h-6 bg-border self-center" />
                                <ReactSelect
                                  options={warrantyOptions}
                                  value={warrantyOptions.find(
                                    (opt) =>
                                      opt.value === productData.warranty_timeframe,
                                  )}
                                  onChange={(opt) =>
                                    setProductData({
                                      ...productData,
                                      warranty_timeframe: opt.value,
                                    })
                                  }
                                  className="flex-1"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      backgroundColor: "transparent",
                                      border: "none",
                                      boxShadow: "none",
                                      minHeight: "32px",
                                      height: "32px",
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: "0 8px",
                                    }),
                                  }}
                                />
                              </div>
                            </div>

                            <div className="flex items-center justify-between p-2.5 px-4 bg-muted/30 rounded-lg border border-border h-[46px]">
                              <div className="space-y-0.5">
                                <Label htmlFor="comm" className="text-sm font-medium cursor-pointer">
                                  Commission
                                </Label>
                                <p className="text-[11px] text-muted-foreground">
                                  Enable sales commission for this product
                                </p>
                              </div>
                              <Switch
                                checked={productData.is_commission}
                                onCheckedChange={(c) =>
                                  setProductData({ ...productData, is_commission: c })
                                }
                                id="comm"
                              />
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Organization */}
            <section ref={organizationRef} id="organization">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 text-primary rounded-lg">
                      <Tag className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Organization</CardTitle>
                      <CardDescription>
                        Categorize and group products
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-8">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label className="font-bold">Categories</Label>
                          {isCategoriesBusy && (
                            <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-normal">
                              <Spinner size={12} className="text-primary" />
                              Loading...
                            </span>
                          )}
                        </div>
                        {hasPermission("category product create") && (
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 h-auto font-bold text-primary hover:text-primary/80"
                            onClick={() =>
                              handleOpenCategoryModal(trimmedCategorySearch)
                            }
                          >
                            {trimmedCategorySearch
                              ? `+ Add "${trimmedCategorySearch}"`
                              : "+ New Category"}
                          </Button>
                        )}
                      </div>
                      <div className="relative">
                        <Input
                          value={categorySearchInput}
                          onChange={(e) =>
                            setCategorySearchInput(e.target.value)
                          }
                          className="pl-9 pr-8 bg-muted/30"
                          placeholder="Search categories..."
                        />
                        {isCategoriesFetching && !isCategoriesLoading ? (
                          <Spinner
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                        ) : (
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        )}
                        {categorySearchInput && (
                          <button
                            type="button"
                            onClick={() => setCategorySearchInput("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded transition-colors"
                            title="Clear search"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="border border-border rounded-xl p-4 h-64 overflow-y-auto bg-muted/10 shadow-inner">
                        {isCategoriesBusy ? (
                          <div className="space-y-3 p-1">
                            <div className="flex items-center justify-between text-xs text-muted-foreground pb-2 border-b border-border/40">
                              <span className="flex items-center gap-1.5 font-medium">
                                <Spinner size={13} className="text-primary" />
                                Loading categories...
                              </span>
                              <span className="text-[11px] text-muted-foreground/70">Please wait</span>
                            </div>
                            <div className="space-y-2.5 animate-pulse pt-1">
                              <div className="flex items-center gap-2.5">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-32 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5 pl-6">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-24 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5 pl-6">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-36 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-28 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5 pl-6">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-40 rounded" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            {hasPermission("category product create") &&
                              trimmedCategorySearch &&
                              !hasExactCategoryMatch &&
                              filteredCategoryTree.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOpenCategoryModal(trimmedCategorySearch)
                                  }
                                  className="w-full flex items-center justify-between p-2.5 mb-3 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 dark:bg-primary/20 dark:hover:bg-primary/25 rounded-xl border border-primary/20 transition-colors shadow-2xs"
                                >
                                  <span className="flex items-center gap-1.5 truncate">
                                    <Plus className="h-3.5 w-3.5 flex-shrink-0" />
                                    Add category &quot;
                                    <span className="font-semibold truncate">
                                      {trimmedCategorySearch}
                                    </span>
                                    &quot;
                                  </span>
                                  <span className="text-[11px] font-semibold underline ml-2 flex-shrink-0">
                                    Create
                                  </span>
                                </button>
                              )}

                            {filteredCategoryTree.length > 0 ? (
                              renderCategoryCheckboxes(filteredCategoryTree)
                            ) : (
                              <div className="h-full flex flex-col items-center justify-center p-4 text-center">
                                <Tag className="h-8 w-8 mb-2 opacity-40 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground mb-3">
                                  {trimmedCategorySearch ? (
                                    <>
                                      No categories found matching &quot;
                                      <span className="font-semibold text-foreground">
                                        {trimmedCategorySearch}
                                      </span>
                                      &quot;
                                    </>
                                  ) : (
                                    "No categories available"
                                  )}
                                </p>
                                {hasPermission("category product create") &&
                                  trimmedCategorySearch && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
                                      onClick={() =>
                                        handleOpenCategoryModal(
                                          trimmedCategorySearch,
                                        )
                                      }
                                    >
                                      <Plus className="h-4 w-4 mr-1.5" />
                                      Add &quot;{trimmedCategorySearch}&quot;
                                    </Button>
                                  )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="font-bold">Brand / Make</Label>
                          {hasPermission("brand create") && (
                            <Button
                              variant="link"
                              className="p-0 h-auto text-xs font-bold text-primary hover:text-primary/80"
                              onClick={() => setShowBrandModal(true)}
                            >
                              + New Brand
                            </Button>
                          )}
                        </div>
                        <ReactSelect
                          options={brands.map((b) => ({
                            value: String(b.id),
                            label: b.name,
                          }))}
                          value={
                            productData.brand_id
                              ? {
                                value: String(productData.brand_id),
                                label:
                                  brandLabels[String(productData.brand_id)] ||
                                  brands.find(
                                    (b) =>
                                      String(b.id) ===
                                      String(productData.brand_id),
                                  )?.name ||
                                  productDataFromQuery?.brand?.name ||
                                  "Select Brand",
                              }
                              : null
                          }
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              brand_id: opt?.value ?? "",
                              model_id: "",
                            })
                          }
                          onInputChange={(value) => setBrandSearchInput(value)}
                          isLoading={isBrandsFetching}
                          filterOption={() => true}
                          placeholder="Search brands..."
                          isClearable
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="font-bold">Model</Label>
                          {hasPermission("brand model create") && (
                            <Button
                              disabled={!productData.brand_id}
                              variant="link"
                              className="p-0 h-auto text-xs font-bold text-primary hover:text-primary/80 disabled:opacity-30"
                              onClick={() => setShowModelModal(true)}
                            >
                              + New Model
                            </Button>
                          )}
                        </div>
                        <ReactSelect
                          options={models.map((m) => ({
                            value: String(m.id),
                            label: m.name,
                          }))}
                          value={
                            productData.model_id
                              ? {
                                value: String(productData.model_id),
                                label:
                                  modelLabels[String(productData.model_id)] ||
                                  models.find(
                                    (m) =>
                                      String(m.id) ===
                                      String(productData.model_id),
                                  )?.name ||
                                  productDataFromQuery?.model?.name ||
                                  "Select Model",
                              }
                              : null
                          }
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              model_id: opt?.value || "",
                            })
                          }
                          onInputChange={(value) => setModelSearchInput(value)}
                          isLoading={isModelsFetching}
                          filterOption={() => true}
                          placeholder="Search models..."
                          isClearable
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="font-bold">Unit</Label>
                          {hasPermission("unit create") && (
                            <Button
                              variant="link"
                              className="p-0 h-auto text-xs font-bold text-primary hover:text-primary/80"
                              onClick={() => setShowUnitModal(true)}
                            >
                              + New Unit
                            </Button>
                          )}
                        </div>
                        <ReactSelect
                          options={units.map((u) => ({
                            value: String(u.id),
                            label: u.name,
                          }))}
                          value={
                            productData.unit_id
                              ? {
                                value: String(productData.unit_id),
                                label:
                                  unitLabels[String(productData.unit_id)] ||
                                  units.find(
                                    (u) =>
                                      String(u.id) ===
                                      String(productData.unit_id),
                                  )?.name ||
                                  productDataFromQuery?.unit?.name ||
                                  "Select Unit",
                              }
                              : null
                          }
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              unit_id: opt?.value || "",
                            })
                          }
                          onInputChange={(value) => setUnitSearchInput(value)}
                          isLoading={isUnitsFetching}
                          filterOption={() => true}
                          placeholder="Search units..."
                          isClearable
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Pricing */}
            <section ref={pricingRef} id="pricing">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100/10 text-emerald-600 dark:text-emerald-400 rounded-lg">
                      <DollarSign className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Pricing & Tax</CardTitle>
                      <CardDescription>
                        Update values and tax logic
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {hasPermission("product view_cost_price") && (
                      <div className="space-y-2">
                        <Label>
                          Cost Price ({currencySymbol}) *
                        </Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="number"
                            step="0.01"
                            className="pl-9 bg-muted/30"
                            value={productData.cost}
                            onChange={(e) => handleCostChange(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Markup (%)</Label>
                        {costPriceNum > 0 && sellPriceNum > 0 && (
                          <span className="text-[11px] text-muted-foreground font-medium">
                            Profit: {currencySymbol}
                            {(sellPriceNum - costPriceNum).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="pl-9 bg-muted/30"
                          value={markupPercent}
                          onChange={(e) => handleMarkupChange(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Selling Price ({currencySymbol}) *
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          className="pl-9 bg-muted/30"
                          value={productData.sell_price}
                          onChange={(e) => handleSellingPriceChange(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Auto-Sync Min/Max Price Checkbox & Fields */}
                  <div className="p-4 bg-muted/20 rounded-xl border border-border/60 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sync-min-max-prices"
                        checked={useSamePriceForMinMax}
                        onCheckedChange={handleToggleSamePriceForMinMax}
                      />
                      <Label
                        htmlFor="sync-min-max-prices"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Use selling price as minimum and maximum selling price
                      </Label>
                    </div>

                    {!useSamePriceForMinMax && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 animate-in fade-in duration-200">
                        <div className="space-y-2">
                          <Label>Min Selling Price ({currencySymbol})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-muted/30"
                            value={productData.sell_price_min}
                            onChange={(e) =>
                              setProductData({
                                ...productData,
                                sell_price_min: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Max Selling Price ({currencySymbol})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-muted/30"
                            value={productData.sell_price_max}
                            onChange={(e) =>
                              setProductData({
                                ...productData,
                                sell_price_max: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 p-6 bg-emerald-500/5 rounded-2xl border border-emerald-500/10">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3.5 bg-background/60 rounded-xl border border-emerald-500/10">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-semibold">
                            Is Taxable
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Enable if this product is subject to sales tax
                          </p>
                        </div>
                        <Switch
                          checked={productData.is_taxable}
                          onCheckedChange={(checked) =>
                            setProductData({
                              ...productData,
                              is_taxable: checked,
                            })
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">
                          Tax Method
                        </Label>
                        <ReactSelect
                          options={taxMethodOptions}
                          value={taxMethodOptions.find(
                            (opt) => opt.value === productData.tax_method,
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              tax_method: opt.value,
                            })
                          }
                          isDisabled={!productData.is_taxable}
                          placeholder="Select Method"
                        />
                        <p className="text-[11px] text-muted-foreground italic">
                          Exclusive: Tax added on top of selling price. Inclusive: Selling price already includes tax.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col justify-between p-4 bg-background/60 rounded-xl border border-emerald-500/10 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Store Tax Rate
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {currentStore?.name ? currentStore.name : "Active Store"}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs font-semibold px-2.5 py-1",
                            productData.is_taxable && storeChargesTax
                              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                              : "bg-muted text-muted-foreground border-border",
                          )}
                        >
                          {productData.is_taxable && storeChargesTax
                            ? `${storeTaxPercentage}% Tax Rate`
                            : productData.is_taxable
                              ? "0% (Store Tax-Exempt)"
                              : "Product Tax-Exempt"}
                        </Badge>
                      </div>

                      <div className="pt-3 border-t border-border/50 space-y-2">
                        {productData.is_taxable && storeChargesTax ? (
                          <>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">
                                {productData.tax_method === "inclusive"
                                  ? `Included Tax (${storeTaxPercentage}%):`
                                  : `Added Tax (${storeTaxPercentage}%):`}
                              </span>
                              <span className="font-semibold text-foreground">
                                {currencySymbol}{taxAmount.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs font-medium">
                              <span className="text-muted-foreground">
                                {productData.tax_method === "inclusive"
                                  ? "Pre-tax Price:"
                                  : "Total Customer Price:"}
                              </span>
                              <span className="text-sm font-bold text-emerald-600">
                                {currencySymbol}
                                {productData.tax_method === "inclusive"
                                  ? (sellPriceNum - taxAmount).toFixed(2)
                                  : finalPriceWithTax.toFixed(2)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground leading-relaxed italic">
                            {!productData.is_taxable
                              ? "Product is marked as tax-exempt. No tax will be applied at checkout."
                              : "Current store has 0% tax or no tax class configured in store settings."}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Inventory */}
            <section ref={inventoryRef} id="inventory">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 text-blue-500 rounded-lg">
                      <Layers className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Inventory Settings</CardTitle>
                      <CardDescription>
                        Thresholds and tracking parameters
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-6">
                  <div className="grid md:grid-cols-2 gap-8 py-2">
                    <div className="p-5 rounded-2xl bg-muted/30 border border-border flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Label className="text-base font-bold text-foreground">
                            Manage Inventory
                          </Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">
                                Track and subtract base stock on sales
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <p className="text-xs text-muted-foreground font-medium">
                          Enable automatic stock subtraction
                        </p>
                      </div>
                      <Switch
                        checked={productData.manage_inventory}
                        onCheckedChange={(c) =>
                          setProductData({
                            ...productData,
                            manage_inventory: c,
                          })
                        }
                      />
                    </div>
                    <div className="p-5 rounded-2xl bg-muted/30 border border-border space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Label className="text-base font-bold">
                              Tracking Type
                            </Label>
                          </div>
                          <p className="text-xs text-muted-foreground italic">
                            {productDataFromQuery?.tracking_type &&
                              productDataFromQuery.tracking_type !== "none"
                              ? "Tracking method is locked to ensure data integrity"
                              : "How individual stock items are identified (none, standard, serial)"}
                          </p>
                        </div>
                        <ReactSelect
                          options={trackingTypeOptions}
                          isDisabled={
                            productDataFromQuery?.tracking_type &&
                            productDataFromQuery.tracking_type !== "none"
                          }
                          value={trackingTypeOptions.find(
                            (opt) =>
                              opt.value ===
                              (productData.tracking_type ||
                                (productData.is_serialized
                                  ? "serial"
                                  : "none")),
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              tracking_type: opt.value,
                              is_serialized: opt.value === "serial",
                            })
                          }
                          className="w-[180px]"
                          placeholder="Select type"
                        />
                      </div>
                    </div>
                  </div>

                  {IS_SUPERMARKET && (
                    <div className="p-6 bg-muted/30 rounded-2xl border border-border">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="bg-amber-500/10 p-2 rounded-lg">
                            <AlertCircle className="h-5 w-5 text-amber-500" />
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground">
                              Expiration Tracking
                            </h3>
                            <p className="text-[11px] text-muted-foreground italic">
                              Track expiry dates and automatic discount tiers
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={productData.is_expirable}
                          onCheckedChange={(c) =>
                            setProductData({ ...productData, is_expirable: c })
                          }
                        />
                      </div>

                      {productData.is_expirable && (
                        <div className="space-y-4 animate-in zoom-in-95 duration-300 border-t border-border/50 pt-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-bold text-foreground">
                              Discount Tiers Before Expiry
                            </Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setProductData({
                                  ...productData,
                                  expiry_discount_tiers: [
                                    ...(productData.expiry_discount_tiers ||
                                      []),
                                    { days_before: 1, discount_percent: 0 },
                                  ],
                                });
                              }}
                            >
                              <Plus className="h-4 w-4 mr-1" /> Add Tier
                            </Button>
                          </div>

                          {(productData.expiry_discount_tiers || []).map(
                            (tier, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-4"
                              >
                                <div className="flex-1 space-y-1">
                                  <Label className="text-xs">Days Before</Label>
                                  <Input
                                    type="number"
                                    min={1}
                                    value={tier.days_before}
                                    onChange={(e) => {
                                      const tiers = [
                                        ...(productData.expiry_discount_tiers ||
                                          []),
                                      ];
                                      tiers[idx].days_before =
                                        parseInt(e.target.value) || 1;
                                      setProductData({
                                        ...productData,
                                        expiry_discount_tiers: tiers,
                                      });
                                    }}
                                  />
                                </div>
                                <div className="flex-1 space-y-1">
                                  <Label className="text-xs">Discount %</Label>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={tier.discount_percent}
                                    onChange={(e) => {
                                      const tiers = [
                                        ...(productData.expiry_discount_tiers ||
                                          []),
                                      ];
                                      tiers[idx].discount_percent =
                                        parseFloat(e.target.value) || 0;
                                      setProductData({
                                        ...productData,
                                        expiry_discount_tiers: tiers,
                                      });
                                    }}
                                  />
                                </div>
                                <div className="pt-5">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="text-muted-foreground hover:text-destructive"
                                    onClick={() => {
                                      const tiers = (
                                        productData.expiry_discount_tiers || []
                                      ).filter((_, i) => i !== idx);
                                      setProductData({
                                        ...productData,
                                        expiry_discount_tiers: tiers,
                                      });
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {productData.manage_inventory && (
                    <div className="p-6 bg-muted/30 rounded-2xl border border-border flex items-end justify-between">
                      <div className="space-y-2 flex-1 max-w-sm">
                        <Label className="font-bold">
                          Low Stock Alert
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          System warns when stock hits this quantity
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Input
                          type="number"
                          className="w-32 bg-background border-border"
                          value={productData.alert_quantity}
                          onChange={(e) =>
                            setProductData({
                              ...productData,
                              alert_quantity: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* Stock Adjustment */}
            {productData.manage_inventory && productData.tracking_type !== "none" && (
              <section ref={stockAdjustmentRef} id="stockAdjustment">
                <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                  <div className="h-1 bg-primary" />
                  <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100/10 text-orange-500 dark:text-orange-400 rounded-lg">
                          <ClipboardList className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle>Stock Adjustments</CardTitle>
                          <CardDescription>
                            View and create stock adjustments for this product
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasPermission("report serial barcode view") &&
                          Boolean(productDataFromQuery?.manage_inventory ?? productData.manage_inventory) &&
                          (productDataFromQuery?.tracking_type === "serial" ||
                            productData.tracking_type === "serial" ||
                            Boolean(productDataFromQuery?.is_serialized || productData.is_serialized)) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() => handleOpenSerialBarcodeModal(null)}
                            >
                              <ScanBarcode className="h-4 w-4" />
                              Serial Barcode
                            </Button>
                          )}
                        {hasPermission("stock adjustment create") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={openAdjustmentModal}
                          >
                            <Package className="h-4 w-4" />
                            Update Stock
                          </Button>
                        )}
                      </div>
                    </div>
                    {!productDataFromQuery?.manage_inventory && (
                      <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-2">
                        <AlertCircle className="h-3 w-3" />
                        Enable inventory management to create stock adjustments
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="pt-6">
                    {isAdjHistoryLoading ? (
                      <div className="flex justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    ) : adjHistory.length > 0 ? (
                      <div className="rounded-md border overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Adj #</th>
                              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Reason</th>
                              <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Qty Change</th>
                              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                              <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {adjHistory.map((adj) => {
                              const itemData =
                                adj.items?.find((item) => String(item.product_id) === String(productDataFromQuery?.id)) ||
                                adj.items?.[0] ||
                                {};
                              const qty = parseFloat(itemData.adjustment_quantity || 0);
                              const isPositive = itemData.adjustment_type === "increase" || qty >= 0;
                              const rowSerials = Array.isArray(itemData.serial_numbers) ? itemData.serial_numbers : [];
                              const isProcessed = String(adj.status || "").toLowerCase() === "processed";
                              const serialCount = rowSerials.length || (itemData.serial_number_count > 0 ? itemData.serial_number_count : 0);
                              const hasSerials = serialCount > 0;
                              const isSerialProduct =
                                productData.tracking_type === "serial" ||
                                itemData.has_serial_numbers ||
                                hasSerials;
                              const canViewSerials = isSerialProduct && isProcessed && hasSerials;

                              return (
                                <tr key={adj.id} className="hover:bg-muted/30 transition-colors">
                                  <td className="px-4 py-3 font-medium">
                                    {canViewSerials ? (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedAdjForSerials(adj);
                                          setSerialSearchTerm("");
                                        }}
                                        className="font-semibold text-primary hover:text-primary/80 hover:underline inline-flex items-center gap-1.5 transition-colors cursor-pointer text-left group"
                                        title="Click to view serial numbers"
                                      >
                                        <ScanBarcode className="h-3.5 w-3.5 text-primary/70 group-hover:text-primary transition-colors shrink-0" />
                                        <span>{adj.adjustment_number || adj.id?.substring(0, 8)}</span>
                                        <Badge
                                          variant="secondary"
                                          className="text-[10px] font-mono h-4 px-1.5 py-0 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                                        >
                                          {serialCount}
                                        </Badge>
                                      </button>
                                    ) : (
                                      <span
                                        className="text-foreground font-medium"
                                        title={
                                          isSerialProduct && !isProcessed
                                            ? "Serial numbers available once adjustment is processed"
                                            : undefined
                                        }
                                      >
                                        {adj.adjustment_number || adj.id?.substring(0, 8)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 capitalize">
                                    {adj.reason_label || (adj.reason || "").replace(/_/g, " ")}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex flex-col items-end">
                                      <span className={`font-bold flex items-center gap-1 justify-end ${isPositive ? "text-green-600" : "text-red-600"}`}>
                                        {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                        {isPositive ? "+" : ""}{qty}
                                      </span>
                                      {itemData.current_quantity !== undefined && itemData.new_quantity !== undefined && (
                                        <span className="text-[11px] text-muted-foreground font-mono">
                                          {itemData.current_quantity} &rarr; {itemData.new_quantity}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Badge
                                      variant="outline"
                                      className={cn("capitalize text-[10px] font-semibold px-2 py-0.5", statusVariantMap[adj.status] || statusVariantMap.draft)}
                                    >
                                      {adj.status_label || (adj.status || "").replace(/_/g, " ")}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 text-muted-foreground text-xs">
                                    <div>{formatDate(adj.created_at)}</div>
                                    {adj.creator?.username && (
                                      <div className="text-[10px] text-muted-foreground/70">
                                        by {adj.creator.username}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {canViewSerials && hasPermission("report serial barcode view") ? (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-7 px-2.5 text-xs gap-1.5 text-primary border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors"
                                        onClick={() => handleOpenSerialBarcodeModal(rowSerials)}
                                        title="Generate serial barcodes for this adjustment"
                                      >
                                        <ScanBarcode className="h-3.5 w-3.5" />
                                        <span>Barcode</span>
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-muted-foreground/40">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/20 rounded-xl border-2 border-dashed border-border/50">
                        <div className="p-4 bg-background rounded-full shadow-sm mb-3">
                          <ClipboardList className="h-8 w-8 text-muted-foreground/30" />
                        </div>
                        <h3 className="text-sm font-bold text-foreground">No Adjustment History</h3>
                        <p className="text-xs text-muted-foreground max-w-[240px] mt-1">
                          No stock adjustments have been made for this product yet.
                        </p>
                        {hasPermission("stock adjustment create") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4 gap-2"
                            onClick={openAdjustmentModal}
                          >
                            <Package className="h-4 w-4" />
                            Update Stock
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Inventory Transactions */}
            {Boolean(productData.manage_inventory) &&
              productData.tracking_type &&
              productData.tracking_type !== "none" && (
                <section ref={transactionsRef} id="transactions">
                  <ProductTransactions
                    productId={id}
                    currencySymbol={currencySymbol}
                  />
                </section>
              )}

            {/* Media */}
            <section ref={mediaRef} id="media">
              <Card className="shadow-lg border-border/60 overflow-hidden">
                <CardHeader className="bg-muted/10 pb-6 border-b flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-muted rounded-lg text-muted-foreground">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Media Library</CardTitle>
                      <CardDescription>Manage product gallery</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {/* Existing Media */}
                    {productData.media?.map((m, index) => (
                      <div
                        key={m.id}
                        className="group relative aspect-square border-2 border-border rounded-2xl overflow-hidden bg-card hover:border-primary transition-all duration-300"
                      >
                        <img
                          src={m.url || m.original_url}
                          alt={`Product ${index}`}
                          className="w-full h-full object-contain p-2 transition duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2">
                          <Button
                            variant="destructive"
                            size="icon"
                            className="h-8 w-8 rounded-full shadow-xl"
                            onClick={() => removeMediaMutation.mutate(m.id)}
                            disabled={removeMediaMutation.status === "pending"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {/* New Selections */}
                    {selectedImagesDetails.map((m, index) => (
                      <div
                        key={m.id}
                        className="group relative aspect-square border-2 border-dashed border-primary/50 rounded-2xl overflow-hidden bg-primary/5 hover:border-primary transition-all duration-300"
                      >
                        <img
                          src={m.url}
                          alt={`New Product ${index}`}
                          className="w-full h-full object-contain p-2 transition duration-500 group-hover:scale-110 opacity-70"
                        />
                        <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2">
                          <Button
                            variant="destructive"
                            size="icon"
                            className="h-8 w-8 rounded-full shadow-xl"
                            onClick={() => {
                              setSelectedImagesDetails((prev) =>
                                prev.filter((item) => item.id !== m.id),
                              );
                              setProductData((prev) => ({
                                ...prev,
                                images: (prev.images || []).filter(
                                  (id) => id !== m.id,
                                ),
                              }));
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-[8px] font-black px-1.5 py-0.5 rounded uppercase">
                          New
                        </div>
                      </div>
                    ))}

                    <div
                      className="group aspect-square border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all duration-300"
                      onClick={() => setIsMediaLibraryOpen(true)}
                    >
                      <div className="p-3 bg-muted text-muted-foreground rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500">
                        <Plus className="h-6 w-6" />
                      </div>
                      <span className="mt-2 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                        Add Image
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>
        </div>
      </div>

      {/* Stock Adjustment Modal */}
      <ProductStockAdjustmentModal
        open={showAdjustmentModal}
        onOpenChange={setShowAdjustmentModal}
        product={productDataFromQuery}
        onSuccess={() => {
          queryClient.invalidateQueries(["stock-adj-history", id, currentStore?.id]);
          queryClient.invalidateQueries(["batches", id, currentStore?.id]);
        }}
      />

      {/* Serial Barcode Modal */}
      <ProductSerialBarcodeModal
        open={showSerialBarcodeModal}
        onOpenChange={(val) => {
          setShowSerialBarcodeModal(val);
          if (!val) {
            setSerialBarcodeInitialSerials(null);
            setSerialBarcodeInitialMode(null);
          }
        }}
        product={productDataFromQuery || productData}
        initialSerials={serialBarcodeInitialSerials}
        initialMode={serialBarcodeInitialMode}
      />

      {/* Serial Numbers Modal for Stock Adjustment */}
      <Modal
        open={Boolean(selectedAdjForSerials)}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelectedAdjForSerials(null);
            setSerialSearchTerm("");
          }
        }}
        title={`Serial Numbers - ${selectedAdjForSerials?.adjustment_number || "Adjustment"}`}
        icon={ScanBarcode}
        maxWidth="max-w-2xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <span className="text-xs text-muted-foreground">
              Total:{" "}
              <strong className="text-foreground">
                {(() => {
                  const item =
                    selectedAdjForSerials?.items?.find(
                      (it) => String(it.product_id) === String(id)
                    ) || selectedAdjForSerials?.items?.[0];
                  return Array.isArray(item?.serial_numbers)
                    ? item.serial_numbers.length
                    : (item?.serial_number_count || 0);
                })()}
              </strong>{" "}
              serial number
              {(() => {
                const item =
                  selectedAdjForSerials?.items?.find(
                    (it) => String(it.product_id) === String(id)
                  ) || selectedAdjForSerials?.items?.[0];
                const count = Array.isArray(item?.serial_numbers)
                  ? item.serial_numbers.length
                  : (item?.serial_number_count || 0);
                return count !== 1 ? "s" : "";
              })()}
            </span>
            <div className="flex items-center gap-2">
              {(() => {
                const item =
                  selectedAdjForSerials?.items?.find(
                    (it) => String(it.product_id) === String(id)
                  ) || selectedAdjForSerials?.items?.[0];
                const serials = Array.isArray(item?.serial_numbers)
                  ? item.serial_numbers
                  : [];
                if (serials.length === 0) return null;
                const filtered = serialSearchTerm.trim()
                  ? serials.filter((s) => {
                    const snStr = typeof s === "object" && s !== null
                      ? s.serial_number || s.serial || ""
                      : String(s);
                    return snStr.toLowerCase().includes(serialSearchTerm.toLowerCase().trim());
                  })
                  : serials;
                return (
                  <>
                    {hasPermission("report serial barcode view") && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-primary border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors"
                        onClick={() => {
                          if (!filtered || filtered.length === 0) return;
                          handleOpenSerialBarcodeModal(filtered);
                        }}
                        title="Generate barcode labels for these serial numbers"
                      >
                        <ScanBarcode className="h-3.5 w-3.5" />
                        Serial Barcode {filtered.length !== serials.length ? `(${filtered.length})` : `(${serials.length})`}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        if (!filtered || filtered.length === 0) return;
                        const copyText = filtered
                          .map((s) => (typeof s === "object" && s !== null ? s.serial_number || s.serial : String(s)))
                          .join("\n");
                        navigator.clipboard.writeText(copyText);
                        toast.success(
                          `Copied ${filtered.length} serial number${filtered.length !== 1 ? "s" : ""
                          } to clipboard`
                        );
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy {filtered.length !== serials.length ? "Filtered" : "All"} ({filtered.length})
                    </Button>
                  </>
                );
              })()}
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setSelectedAdjForSerials(null);
                  setSerialSearchTerm("");
                }}
              >
                Close
              </Button>
            </div>
          </div>
        }
      >
        {selectedAdjForSerials && (() => {
          const adjItem =
            selectedAdjForSerials.items?.find(
              (it) => String(it.product_id) === String(id)
            ) ||
            selectedAdjForSerials.items?.[0] ||
            {};
          const rawSerials = Array.isArray(adjItem.serial_numbers)
            ? adjItem.serial_numbers
            : [];
          const allSerials = rawSerials.map((s) =>
            typeof s === "object" && s !== null
              ? s.serial_number || s.serial || JSON.stringify(s)
              : String(s)
          );
          const filteredSerials = serialSearchTerm.trim()
            ? allSerials.filter((s) =>
              String(s).toLowerCase().includes(serialSearchTerm.toLowerCase().trim())
            )
            : allSerials;
          const qty = parseFloat(adjItem.adjustment_quantity || 0);
          const isPositive = adjItem.adjustment_type === "increase" || qty >= 0;

          return (
            <div className="space-y-5">
              {/* Adjustment Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 bg-muted/30 rounded-xl border border-border/60">
                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                    Adjustment #
                  </span>
                  <p className="text-xs font-semibold text-primary truncate">
                    {selectedAdjForSerials.adjustment_number || selectedAdjForSerials.id?.substring(0, 8)}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                    Reason
                  </span>
                  <p className="text-xs font-medium capitalize truncate">
                    {selectedAdjForSerials.reason_label ||
                      (selectedAdjForSerials.reason || "").replace(/_/g, " ")}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                    Qty Change
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`text-xs font-bold flex items-center gap-1 ${isPositive ? "text-green-600" : "text-red-600"
                        }`}
                    >
                      {isPositive ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {isPositive ? "+" : ""}
                      {qty}
                    </span>
                    {adjItem.current_quantity !== undefined && adjItem.new_quantity !== undefined && (
                      <span className="text-[10px] text-muted-foreground font-mono">
                        ({adjItem.current_quantity} &rarr; {adjItem.new_quantity})
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                    Status
                  </span>
                  <div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "capitalize text-[10px] font-semibold px-2 py-0.5",
                        statusVariantMap[selectedAdjForSerials.status] ||
                        statusVariantMap.draft
                      )}
                    >
                      {selectedAdjForSerials.status_label ||
                        (selectedAdjForSerials.status || "").replace(/_/g, " ")}
                    </Badge>
                  </div>
                </div>

                {/* Additional Details from SAD Response: Creator, Store, Date */}
                {(selectedAdjForSerials.created_at || selectedAdjForSerials.creator?.username || selectedAdjForSerials.store?.name) && (
                  <div className="col-span-2 sm:col-span-4 pt-2 border-t border-border/40 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    {selectedAdjForSerials.created_at && (
                      <span>
                        Date: <strong className="text-foreground font-medium">{formatDate(selectedAdjForSerials.created_at)}</strong>
                      </span>
                    )}
                    {selectedAdjForSerials.creator?.username && (
                      <span>
                        By: <strong className="text-foreground font-medium">{selectedAdjForSerials.creator.username}</strong>
                      </span>
                    )}
                    {selectedAdjForSerials.store?.name && (
                      <span>
                        Store: <strong className="text-foreground font-medium">{selectedAdjForSerials.store.name}</strong>
                      </span>
                    )}
                  </div>
                )}

                {(selectedAdjForSerials.notes || adjItem.reason_notes) && (
                  <div className="col-span-2 sm:col-span-4 pt-1.5 border-t border-border/40 text-xs text-muted-foreground italic">
                    <span className="font-semibold not-italic text-foreground text-[11px] mr-1.5">
                      Notes:
                    </span>
                    {selectedAdjForSerials.notes || adjItem.reason_notes}
                  </div>
                )}
              </div>

              {/* Serials Header Toolbar */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search serial numbers..."
                    value={serialSearchTerm}
                    onChange={(e) => setSerialSearchTerm(e.target.value)}
                    className="pl-9 h-9 text-xs"
                  />
                  {serialSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setSerialSearchTerm("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {hasPermission("report serial barcode view") && allSerials.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs text-primary border-primary/30 hover:bg-primary/10 shrink-0 h-9 transition-colors"
                    onClick={() => handleOpenSerialBarcodeModal(filteredSerials)}
                    disabled={filteredSerials.length === 0}
                    title="Generate barcode labels for serial numbers"
                  >
                    <ScanBarcode className="h-3.5 w-3.5" />
                    <span>
                      Serial Barcode {filteredSerials.length !== allSerials.length ? `(${filteredSerials.length})` : `(${allSerials.length})`}
                    </span>
                  </Button>
                )}
              </div>

              {/* Serials List */}
              {allSerials.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/10 rounded-xl border border-dashed border-border/60">
                  <div className="p-3 bg-muted/30 rounded-full mb-3">
                    <ScanBarcode className="h-6 w-6 text-muted-foreground/40" />
                  </div>
                  <h4 className="text-sm font-semibold">No Serial Numbers</h4>
                  <p className="text-xs text-muted-foreground max-w-[280px] mt-1">
                    There are no serial numbers recorded for this adjustment item.
                  </p>
                </div>
              ) : filteredSerials.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center bg-muted/10 rounded-xl border border-dashed border-border/60">
                  <p className="text-xs text-muted-foreground">
                    No serial numbers match &quot;{serialSearchTerm}&quot;
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-xs text-primary"
                    onClick={() => setSerialSearchTerm("")}
                  >
                    Clear Search
                  </Button>
                </div>
              ) : (
                <div className="max-h-[340px] overflow-y-auto pr-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {filteredSerials.map((sn, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2.5 rounded-lg border bg-card hover:bg-muted/40 transition-colors group shadow-2xs"
                      >
                        <div className="flex items-center gap-2 min-w-0 pr-2">
                          <span className="text-[10px] font-mono text-muted-foreground/70 w-6 text-right shrink-0">
                            #{idx + 1}
                          </span>
                          <ScanBarcode className="h-3.5 w-3.5 text-primary/60 shrink-0" />
                          <button
                            type="button"
                            onClick={() => handleOpenSerialTracking(sn)}
                            className="font-mono text-xs font-semibold truncate hover:text-primary hover:underline cursor-pointer text-left transition-colors select-all"
                            title="Track serial movements"
                          >
                            {sn}
                          </button>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-60 group-hover:opacity-100 hover:text-primary transition-opacity"
                            onClick={() => handleOpenSerialTracking(sn)}
                            title="Track serial movements"
                          >
                            <History className="h-3 w-3 text-muted-foreground hover:text-primary" />
                          </Button>
                          {hasPermission("report serial barcode view") && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-60 group-hover:opacity-100 hover:text-primary transition-opacity"
                              onClick={() => handleOpenSerialBarcodeModal([sn])}
                              title="Generate barcode for this serial"
                            >
                              <ScanBarcode className="h-3 w-3 text-primary/70 hover:text-primary" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              navigator.clipboard.writeText(sn);
                              setCopiedSerial(sn);
                              toast.success(`Copied "${sn}"`);
                              setTimeout(() => setCopiedSerial(null), 2000);
                            }}
                            title="Copy serial number"
                          >
                            {copiedSerial === sn ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* Serial Tracking Modal */}
      <SerialDetailModal
        open={showTrackingModal}
        onOpenChange={(isOpen) => {
          setShowTrackingModal(isOpen);
          if (!isOpen) {
            setSelectedTrackingSerial(null);
          }
        }}
        serialId={selectedTrackingSerial?.id}
        serialNumber={
          selectedTrackingSerial?.serial_number ||
          selectedTrackingSerial?.serialNumber
        }
      />

      <ProductCategoryModal
        open={showCategoryModal}
        onOpenChange={(isOpen) => {
          setShowCategoryModal(isOpen);
          if (!isOpen) setCategoryModalInitialName("");
        }}
        initialCategoryName={categoryModalInitialName}
        categories={categories}
        isLoadingCategories={isCategoriesBusy}
        simpleMode
        onSuccess={(newCategory) => {
          if (newCategory?.id) {
            setSelectedCategoryIds((prev) =>
              prev.includes(newCategory.id) ? prev : [...prev, newCategory.id],
            );
          }
          setCategorySearchInput("");
          setCategoryModalInitialName("");
        }}
      />
      <Dialog open={showBrandModal} onOpenChange={setShowBrandModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label>Brand Name</Label>
              <Input
                value={brandFormData.name}
                onChange={(e) =>
                  setBrandFormData({ ...brandFormData, name: e.target.value })
                }
              />
            </div>
            <Button
              className="w-full h-11"
              onClick={() => {
                if (brandFormData.name)
                  createBrandMutation.mutate(brandFormData);
              }}
              disabled={createBrandMutation.isPending}
            >
              Confirm Brand
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showUnitModal} onOpenChange={setShowUnitModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Unit</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label>Unit Name</Label>
              <Input
                value={unitFormData.name}
                onChange={(e) =>
                  setUnitFormData({ ...unitFormData, name: e.target.value })
                }
              />
            </div>
            <Button
              className="w-full h-11"
              onClick={() => {
                if (unitFormData.name) createUnitMutation.mutate(unitFormData);
              }}
              disabled={createUnitMutation.isPending}
            >
              Save Unit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showModelModal} onOpenChange={setShowModelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Model</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label>Model Name</Label>
              <Input
                value={modelFormData.name}
                onChange={(e) =>
                  setModelFormData({
                    ...modelFormData,
                    name: e.target.value,
                    brand_id: productData.brand_id,
                  })
                }
              />
            </div>
            <Button
              className="w-full h-11"
              onClick={() => {
                if (modelFormData.name)
                  createModelMutation.mutate(modelFormData);
              }}
              disabled={createModelMutation.isPending}
            >
              Save Model
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detailed Description Modal */}
      <Dialog open={showDescriptionModal} onOpenChange={setShowDescriptionModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Detailed Product Description
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              Add rich technical specifications, features, formatting and visual content.
            </p>
            <DefaultEditor
              value={productData.description || ""}
              onChange={(e) =>
                setProductData({
                  ...productData,
                  description: e.target.value,
                })
              }
              className="min-h-[300px] rounded-xl border-border overflow-hidden"
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button type="button" onClick={() => setShowDescriptionModal(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <MediaLibraryComponent
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={(ids, details) => {
          const selectedDetails = Array.isArray(details) ? details : [details];
          const selectedIds = Array.isArray(ids) ? ids : [ids];

          setSelectedImagesDetails((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewDetails = selectedDetails.filter(
              (item) => !existingIds.has(item.id),
            );
            return [...prev, ...uniqueNewDetails];
          });

          setProductData((prev) => ({
            ...prev,
            images: [...new Set([...(prev.images || []), ...selectedIds])],
            // keeping image field for compatibility if needed elsewhere
            image:
              (prev.images || []).length > 0 ? prev.images[0] : selectedIds[0],
          }));

          setIsMediaLibraryOpen(false);
        }}
        selectionMode="multiple"
      />
    </div>
  );
};

export default EditProduct;
